import {DateStringNullable, Dictionary, NullableKeys, todayDate} from '@fl/cmsch-fe-library';
import {opt} from 'ts-opt';

import {allOptionValueArray} from '../constants/consts';

/** Form name used by the breeder management modal. */
export const subjectManagementFacilityFormName = 'subjectManagementFacilityForm';

/** Admin breeder/organization management form type. */
export type SubjectManagementFacilityFormTag =
    'ValidateCreateBreederBindingManager' | 'ValidateUpdateBreederBindingManager';

/** Admin breeder/organization management form type. */
export interface SubjectManagementFacilityFormValues {
    /** Approval start date. */
    approvedFrom: DateStringNullable;
    /** Approval end date. */
    approvedTo: DateStringNullable;
    /** Selected center ids. */
    centerIds: Array<number>;
    /** Selected farm ids. */
    farmIds: Array<number>;
    /** Selected subject id. */
    subjectId: number;
    /** Selected role ids. */
    roleIds: Array<number>;
    /** Selected user profile id, if any. */
    userProfileId: number | null;
    /** Existing breeder-role binding ids. */
    userProfileRoleIds: Array<number>;
    /** Backend validation tag. */
    tag: SubjectManagementFacilityFormTag;
}

/**
 * Default values for the breeder management modal.
 *
 * @param userProfileId Selected user profile id, if any.
 * @param roleId Selected role id, if any.
 * @param subjectId Selected subject id, if any.
 * @param allFarms Whether all farms are selected.
 * @param allCenters Whether all centers are selected.
 * @returns Default breeder-management form values.
 */
export const subjectManagementFacilityFormInitialValues = (
    userProfileId: number | null,
    roleId: number | null,
    subjectId: number | null,
    allFarms: boolean,
    allCenters: boolean,
): NullableKeys<SubjectManagementFacilityFormValues> => ({
    approvedFrom: todayDate(),
    approvedTo: null,
    centerIds: allCenters ? allOptionValueArray : null,
    farmIds: allFarms ? allOptionValueArray : null,
    subjectId,
    roleIds: opt(roleId).toArray(),
    userProfileId,
    userProfileRoleIds: [],
    tag: 'ValidateCreateBreederBindingManager',
});

/** Admin breeder/organization management form type. */
export interface SubjectManagementFacilityFormEntryData {
    /** Approval start date. */
    approvedFrom: DateStringNullable;
    /** Approval end date. */
    approvedTo: DateStringNullable;
    /** Selected subject id. */
    subjectId: number;
    /** Selected role ids. */
    roleIds: Array<number>;
    /** Selected user profile id, if any. */
    userProfileId: number | null;
    /** Existing breeder-role binding ids. */
    userProfileRoleIds: Array<number>;
    /** Selected center ids. */
    centerIds: Array<number> | null;
    /** Selected farm ids. */
    farmIds: Array<number>;
}

/** Admin breeder/organization management form type. */
export type SubjectManagementFacilityFormValuesErrors = Dictionary<
    keyof SubjectManagementFacilityFormEntryData, string
>;

/** Admin breeder/organization management form type. */
export type SubjectManagementFacilityFormType = 'BREEDER' | 'ORGANIZATION';
